import type { ReactNode } from 'react'
import { Link } from 'react-router'
import { Clock, Megaphone, Plus, Tags } from 'lucide-react'
import { useMe } from '../api/me'
import { RequestFailed } from '../api/client'
import { useCampaigns, type Campaign } from '../api/campaigns'
import { audienceSummary, campaignStatusLabel, campaignStatusVariant } from '../lib/campaigns'
import { fullTime } from '../lib/format'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'

// Owners and admins make campaigns; viewers see them. Agents never reach this
// page (it is not in their menu) and the server refuses them too.
export function CampaignsPage() {
  const me = useMe()
  const list = useCampaigns()
  const admin = me.data?.user.role === 'owner' || me.data?.user.role === 'admin'
  const items = list.data?.pages.flatMap((p) => p.items) ?? []

  return (
    <main className="mx-auto flex max-w-4xl flex-col gap-5 px-4 py-6 sm:py-8">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex min-w-0 flex-[1_1_280px] flex-col gap-1">
          <h1 className="text-2xl font-bold">Campaigns</h1>
          <p className="text-sm text-muted-foreground">
            Send one approved template to many contacts. Only contacts who agreed to receive messages and are not on do-not-message get it.
          </p>
        </div>
        {admin && (
          <Button asChild>
            <Link to="/campaigns/new">
              <Plus aria-hidden="true" />
              New campaign
            </Link>
          </Button>
        )}
      </div>

      {list.isPending ? (
        <div role="status" aria-label="Loading campaigns" className="flex flex-col gap-3">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-20 w-full" />)}
        </div>
      ) : list.isError ? (
        <p role="alert" className="rounded-lg bg-destructive-soft p-3 text-sm text-destructive">{(list.error as RequestFailed).message}</p>
      ) : items.length === 0 ? (
        <Empty>
          No campaigns yet.{' '}
          {admin ? 'Start one with New campaign: pick an approved template and the tags of the contacts who should get it.' : 'An owner or admin can create one.'}
        </Empty>
      ) : (
        <>
          <ul className="divide-y rounded-xl border bg-card">
            {items.map((c) => (
              <li key={c.id}>
                <Link to={`/campaigns/${c.id}`} className="flex flex-col gap-1.5 px-4 py-3 hover:bg-accent">
                  <span className="flex flex-wrap items-center gap-2">
                    <Megaphone className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                    <span className="min-w-0 truncate font-semibold">{c.name}</span>
                    <Badge variant={campaignStatusVariant[c.status]}>{campaignStatusLabel[c.status]}</Badge>
                  </span>
                  <span className="text-sm text-muted-foreground [overflow-wrap:anywhere]">
                    {c.template.name} ({c.template.category}) from {c.displayNumber}
                  </span>
                  <span className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Tags className="size-3.5" aria-hidden="true" />
                      {audienceSummary(c)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="size-3.5" aria-hidden="true" />
                      {listTiming(c)}
                    </span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          {list.hasNextPage && (
            <Button variant="outline" className="self-center" disabled={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
              {list.isFetchingNextPage ? 'Loading' : 'Show more'}
            </Button>
          )}
        </>
      )}
    </main>
  )
}

// One line for when: before launch, the plan; after, how far it got.
function listTiming(c: Campaign): string {
  const p = c.progress
  if (p && c.startedAt) {
    const reached = p.sent + p.delivered + p.read
    const parts = [`${reached.toLocaleString('en-IN')} of ${(p.recipients - p.skipped).toLocaleString('en-IN')} sent`]
    if (p.failed > 0) parts.push(`${p.failed.toLocaleString('en-IN')} failed`)
    if (c.hold === 'dailyLimit') parts.push('waiting for the daily limit')
    else if (c.hold) parts.push('paused')
    return parts.join(', ')
  }
  if (c.status === 'sending') return 'Starting'
  if (c.status === 'cancelled') return 'Cancelled before sending'
  return c.sendAt ? `${c.status === 'scheduled' ? 'Scheduled for' : 'Sends'} ${fullTime(c.sendAt)}` : 'Sends when launched'
}

function Empty({ children }: { children: ReactNode }) {
  return <p className="rounded-xl border border-dashed p-6 text-center text-sm text-muted-foreground">{children}</p>
}
